import { throttleIfPossible } from "@faire/web/common/globals/throttleIfPossible";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { getEstimatedViewportType } from "@faire/web/common/user-agent/getEstimatedViewportType";
import { getWindowWidth } from "@faire/web/ui/hooks/useObservableWindow";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useHandleResize } from "./__internal__/useHandleResize";
import {
  DEFAULT_MIN_WIDTH_BREAKPOINTS,
  MinWidthBreakpoints,
  Viewport,
  getViewportValues,
} from "./viewport-utils/viewport-utils";

export const useViewport = (
  minWidthBreakpoints: MinWidthBreakpoints = DEFAULT_MIN_WIDTH_BREAKPOINTS
): Viewport => {
  const isSSR = useIsSSR();

  const initialViewport = useMemo(() => {
    if (isSSR) {
      return getViewportValues(
        getEstimatedViewportType().width,
        minWidthBreakpoints
      );
    }

    return getViewportValues(getWindowWidth(), minWidthBreakpoints);
  }, [isSSR, minWidthBreakpoints]);

  const [isMobile, setIsMobile] = useState(initialViewport.isMobile);
  const [isTablet, setIsTablet] = useState(initialViewport.isTablet);
  const [isDesktop, setIsDesktop] = useState(initialViewport.isDesktop);
  const [isXLargeDesktop, setIsXLargeDesktop] = useState(
    initialViewport.isXLargeDesktop
  );
  const [isXXLargeDesktop, setIsXXLargeDesktop] = useState(
    initialViewport.isXXLargeDesktop
  );
  const [isTabletAndAbove, setIsTabletAndAbove] = useState(
    initialViewport.isTabletAndAbove
  );
  const [isDesktopAndAbove, setIsDesktopAndAbove] = useState(
    initialViewport.isDesktopAndAbove
  );
  const [isXLargeAndAbove, setIsXLargeAndAbove] = useState(
    initialViewport.isXLargeAndAbove
  );
  const [isTabletAndBelow, setIsTabletAndBelow] = useState(
    initialViewport.isTabletAndBelow
  );

  const handleResize = useCallback(() => {
    const width = getWindowWidth();
    const viewport = getViewportValues(width, minWidthBreakpoints);
    setIsMobile(viewport.isMobile);
    setIsTablet(viewport.isTablet);
    setIsDesktop(viewport.isDesktop);
    setIsXLargeDesktop(viewport.isXLargeDesktop);
    setIsXXLargeDesktop(viewport.isXXLargeDesktop);
    setIsTabletAndAbove(viewport.isTabletAndAbove);
    setIsDesktopAndAbove(viewport.isDesktopAndAbove);
    setIsXLargeAndAbove(viewport.isXLargeAndAbove);
    setIsTabletAndBelow(viewport.isTabletAndBelow);
  }, [minWidthBreakpoints]);

  // SSR correction
  useEffect(() => {
    handleResize();
  }, [handleResize]);

  const throttledHandleResize = useMemo(
    () => throttleIfPossible(handleResize, 200),
    [handleResize]
  );

  useHandleResize(throttledHandleResize);

  return useMemo(
    () => ({
      isMobile,
      isTablet,
      isDesktop,
      isXLargeDesktop,
      isXXLargeDesktop,
      isTabletAndAbove,
      isDesktopAndAbove,
      isXLargeAndAbove,
      isTabletAndBelow,
    }),
    [
      isDesktop,
      isDesktopAndAbove,
      isMobile,
      isTablet,
      isTabletAndAbove,
      isTabletAndBelow,
      isXLargeDesktop,
      isXXLargeDesktop,
      isXLargeAndAbove,
    ]
  );
};
